import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { UnifiedDocumentList } from '@/components/partners/UnifiedDocumentList';
import { AddRatingDialog } from '@/components/partners/AddRatingDialog';
import { RatingsHistoryList } from '@/components/partners/RatingsHistoryList';
import { ContractorPortalUsersSection } from '@/components/partners/ContractorPortalUsersSection';
import { PrequalificationStatusBadge } from '@/components/partners/PrequalificationStatusBadge';
import { RatingDisplay } from '@/components/partners/RatingDisplay';
import { Edit, Star, Building2, FileText, Users } from 'lucide-react';
import { useState } from 'react';

interface Company {
  id: string;
  name: string;
  company_type?: string;
  prequalification_status?: string;
  overall_rating?: number;
  email?: string;
  phone?: string;
  website?: string;
  address_line1?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

interface CompanyDetailPanelProps {
  company: Company | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (company: Company) => void;
  defaultTab?: string;
}

export function CompanyDetailPanel({ company, open, onOpenChange, onEdit, defaultTab = "overview" }: CompanyDetailPanelProps) {
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);

  if (!company) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <SheetTitle className="text-2xl mb-2">{company.name}</SheetTitle>
                <div className="flex items-center gap-2">
                  {company.prequalification_status && (
                    <PrequalificationStatusBadge status={company.prequalification_status} />
                  )}
                  {company.company_type && (
                    <Badge variant="outline" className="capitalize">{company.company_type}</Badge>
                  )}
                  {company.overall_rating && (
                    <div className="flex items-center gap-1">
                      <RatingDisplay rating={company.overall_rating} size="sm" showNumeric={false} />
                    </div>
                  )}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => onEdit(company)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
          </SheetHeader>

          <Tabs defaultValue={defaultTab} className="mt-6">
            <TabsList className="grid w-full grid-cols-4 h-auto p-1 bg-muted/50">
              <TabsTrigger value="overview" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
                <Building2 className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="documents" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
                <FileText className="h-4 w-4" />
                Documents
              </TabsTrigger>
              <TabsTrigger value="ratings" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
                <Star className="h-4 w-4" />
                Ratings
              </TabsTrigger>
              <TabsTrigger value="portal-users" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
                <Users className="h-4 w-4" />
                Portal Users
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold mb-4 pb-2 border-b-2 border-l-4 border-l-primary/50 pl-3 bg-gradient-to-r from-primary/5 to-transparent">Contact Information</h3>
                  <div className="space-y-2">
                    {company.email && (
                      <div>
                        <span className="text-sm font-medium">Email:</span>
                        <p className="text-sm text-muted-foreground">{company.email}</p>
                      </div>
                    )}
                    {company.phone && (
                      <div>
                        <span className="text-sm font-medium">Phone:</span>
                        <p className="text-sm text-muted-foreground">{company.phone}</p>
                      </div>
                    )}
                    {company.website && (
                      <div>
                        <span className="text-sm font-medium">Website:</span>
                        <p className="text-sm text-muted-foreground">{company.website}</p>
                      </div>
                    )}
                  </div>
                </div>

                {(company.address_line1 || company.city) && (
                  <div>
                    <h3 className="text-sm font-semibold mb-4 pb-2 border-b-2 border-l-4 border-l-primary/50 pl-3 bg-gradient-to-r from-primary/5 to-transparent">Address</h3>
                    <div className="text-sm">
                      {company.address_line1 && <p>{company.address_line1}</p>}
                      {(company.city || company.state || company.zip_code) && (
                        <p>
                          {company.city && company.city}
                          {company.state && `, ${company.state}`}
                          {company.zip_code && ` ${company.zip_code}`}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="documents" className="space-y-4">
              <UnifiedDocumentList companyId={company.id} />
            </TabsContent>

            <TabsContent value="ratings" className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => setRatingDialogOpen(true)}>
                  <Star className="h-4 w-4 mr-2" />
                  Add Rating
                </Button>
              </div>
              <RatingsHistoryList companyId={company.id} />
            </TabsContent>

            <TabsContent value="portal-users" className="space-y-4">
              {company.company_type === 'contractor' || company.company_type === 'both' ? (
                <ContractorPortalUsersSection companyId={company.id} />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Portal users are only available for contractors. Change the company type to "Contractor" or "Both" to manage portal access.
                </div>
              )}
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      <AddRatingDialog
        open={ratingDialogOpen}
        onOpenChange={setRatingDialogOpen}
        companyId={company.id}
      />
    </>
  );
}
